import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Priority Processing (Apex) — opt-in cart upsell.
 *
 * Unlike shipping protection, this add-on is OFF by default. The presence of
 * the priority-processing line item is the whole state: the customer toggles it
 * on (adds the line) or off (removes it). We only reconcile to keep the line at
 * qty 1 and to drop it if the cart no longer has any real products.
 */

const SOURCE = 'priority-processing';

// In-flight lock for this add-on's own cart writes.
let busy = false;

class PriorityProcessingComponent extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.checkbox?.addEventListener('change', this.#onToggle, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#reconcile();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  #onCartUpdate = (event) => {
    if (event?.detail?.sourceId === SOURCE) return;
    this.#reconcile();
  };

  #onToggle = async () => {
    if (!this.checkbox) return;
    const wantOn = this.checkbox.checked;
    if (busy) {
      this.checkbox.checked = !wantOn;
      return;
    }
    if (wantOn) {
      await this.#enable();
    } else {
      await this.#disable();
    }
  };

  async #getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async #post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  #notify(cart) {
    document.dispatchEvent(new CartUpdateEvent(cart || {}, SOURCE, {}));
  }

  // Opt-in: never auto-add. Just keep the line tidy if it exists.
  async #reconcile() {
    if (busy || !this.variantId) return;
    busy = true;
    let changed = false;
    let cart = null;

    try {
      cart = await this.#getCart();
      const vid = this.variantId;
      const items = cart.items || [];
      const line = items.find((i) => i.variant_id === vid);

      if (line) {
        const realCount = items.filter((i) => i.variant_id !== vid).length;
        if (realCount === 0) {
          cart = await this.#post('/cart/update.js', { updates: { [vid]: 0 } });
          changed = true;
        } else if (line.quantity !== 1) {
          cart = await this.#post('/cart/update.js', { updates: { [vid]: 1 } });
          changed = true;
        }
      }
    } catch (error) {
      console.error('[priority-processing] reconcile failed', error);
    } finally {
      busy = false;
    }

    if (changed) this.#notify(cart);
  }

  async #enable() {
    if (busy || !this.variantId) return;
    busy = true;
    let cart = null;
    try {
      const vid = this.variantId;
      const current = await this.#getCart();
      const has = (current.items || []).some((i) => i.variant_id === vid);
      if (!has) {
        cart = await this.#post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] });
      } else {
        cart = current;
      }
    } catch (error) {
      console.error('[priority-processing] enable failed', error);
    } finally {
      busy = false;
    }
    this.#notify(cart);
  }

  async #disable() {
    if (busy || !this.variantId) return;
    busy = true;
    let cart = null;
    try {
      const vid = this.variantId;
      cart = await this.#post('/cart/update.js', { updates: { [vid]: 0 } });
    } catch (error) {
      console.error('[priority-processing] disable failed', error);
    } finally {
      busy = false;
    }
    this.#notify(cart);
  }
}

if (!customElements.get('priority-processing-component')) {
  customElements.define('priority-processing-component', PriorityProcessingComponent);
}
